import "./PhoneLogin.scss"
import React, { useEffect, useRef, useState } from "react"
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Input,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  Popover,
  TextField,
  Typography,
} from "@mui/material"
import { useAppDispatch } from "../../../redux/hooks"
import { sendVerificationCodeAsync } from "../../../redux/slices/authSlice"
import { useTranslationLabel } from "../../../hooks/useTranslationLabel"
import useFormValidation from "../../../hooks/useFormValidation"
import { countryCodes } from "../../../config/countryCodes.const"
import Loader from "../../../components/Loader/Loader.component"
import {
  ModalTypes,
  hideModal,
  selectActiveModal,
  showModal,
} from "../../../redux/slices/modalSlice"
import { useSelector } from "react-redux"
import VerifyPhoneModal from "../../../components/Modals/VerifyPhoneModal/VerifyPhoneModal.component"
import { useValidationSchemas } from "../../../hooks/useIsValidationScehemes"
import { useBranding } from "../../../hooks/useBranding"
import { useIsGoTo } from "../../../hooks/useIsGoTo"
import { t } from "i18next"

const PhoneLogin = () => {
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const {
    FORGOT_PASSWORD,
    LOGIN_VERIFY_CODE,
    BTN_CONTINUE,
    INPUT_PHONE_NUMBER,
    REMEMBER_ME,
    INPUT_COUNTRY,
    SEARCH,
  } = useTranslationLabel()
  const activeModal: ModalTypes | null = useSelector(selectActiveModal)
  const [filter, setFilter] = useState("")
  const { phoneLoginSchema } = useValidationSchemas()
  const phoneFormDataInitial = { countryCode: "", phoneNumber: "" }
  const {
    formData: phoneFormData,
    errors: phoneErrors,
    handleChange: handlePhoneChange,
    validateForm: validatePhoneForm,
  } = useFormValidation(phoneFormDataInitial, phoneLoginSchema)
  const { join, registrationLink } = useBranding()
  const { isGoTo } = useIsGoTo()
  const fixedPhoneNumber = useRef("")

  const handleRememberMeChange = (event: any) => {
    setRememberMe(event.target.checked)
  }
  const [anchorEl, setAnchorEl] = useState(null)
  const [countryCode, setCountryCode] = useState("+972")
  const open = Boolean(anchorEl)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)

    if (validatePhoneForm()) {
      const { countryCode, phoneNumber } = phoneFormData
      fixedPhoneNumber.current = phoneNumber
      if (fixedPhoneNumber.current.startsWith("0")) {
        fixedPhoneNumber.current = phoneNumber.substring(1)
      }
      await dispatch(
        sendVerificationCodeAsync({
          phoneNumber: countryCode + fixedPhoneNumber.current,
        }),
      )
        .unwrap()
        .finally(() => {
          dispatch(showModal({ activeModal: ModalTypes.VERIFY_PHONE_MODAL }))
          setLoading(false)
        })

      setLoading(false)
    } else {
      setLoading(false)
    }
  }
  const handleClick = (event: any) => {
    event.preventDefault()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  useEffect(() => {
    handlePhoneChange({
      target: { name: "countryCode", value: countryCode },
    } as any)
  }, [countryCode])

  const handleSelect = (code: string) => {
    setCountryCode(code)
    handlePhoneChange({
      target: { name: "countryCode", value: code },
    } as any)
    handleClose()
  }

  const filteredCountryCodes = countryCodes.filter((code) =>
    code.toLowerCase().includes(filter.toLowerCase()),
  )

  return (
    <div className="phone-login-container">
      {loading && <Loader />}

      <form onSubmit={handleSubmit} className={"form column center"}>
        <div className="form-group phone">
          <>
            <div className="inputs" style={{ direction: "ltr" }}>
              <FormControl
                error={!!phoneErrors.countryCode}
                className="base-input country-code"
              >
                <InputLabel
                  htmlFor="phone-country"
                  className="input-label-phone"
                  disableAnimation
                >
                  {INPUT_COUNTRY}
                </InputLabel>
                <Input
                  id="phone-country"
                  onClick={handleClick}
                  value={countryCode}
                />
                <Popover
                  id="country-code-menu"
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                >
                  <TextField
                    autoFocus
                    placeholder={SEARCH}
                    onChange={(e) => setFilter(e.target.value)}
                  />
                  <List component="nav">
                    {filteredCountryCodes.map((code) => (
                      <ListItem key={code} onClick={() => handleSelect(code)}>
                        <ListItemText primary={code} />
                      </ListItem>
                    ))}
                  </List>
                </Popover>
              </FormControl>
              <FormControl error={!!phoneErrors.phoneNumber}>
                <InputLabel
                  htmlFor="phone-number"
                  className="input-label-phone"
                  disableAnimation
                >
                  {INPUT_PHONE_NUMBER}
                </InputLabel>
                <Input
                  id="phone-number"
                  className="base-input phone-number"
                  type="tel"
                  name="phoneNumber"
                  value={phoneFormData.phoneNumber}
                  onChange={handlePhoneChange}
                  error={!!phoneErrors.phoneNumber}
                />
              </FormControl>
            </div>
            {phoneErrors.phoneNumber && (
              <Typography variant="caption" color="error">
                {phoneErrors.phoneNumber}
              </Typography>
            )}
          </>
          <Typography variant="body2" className="description">
            {LOGIN_VERIFY_CODE}
          </Typography>
        </div>
        <div className="form-options column center">
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={handleRememberMeChange}
              />
            }
            label={REMEMBER_ME}
          />
          {/* <a
            // href="/"
            onClick={() =>
              dispatch(
                showModal({ activeModal: ModalTypes.FORGOT_PASSWORD_MODAL }),
              )
            }
          >
            {FORGOT_PASSWORD}
          </a> */}
        </div>

        <Button
          type="submit"
          disabled={loading}
          variant="contained"
          className="primary-btn"
        >
          {BTN_CONTINUE}
        </Button>
        {isGoTo && (
          <div className="form-options column center join">
            <a href={registrationLink} target="_blank">
              {t(join)}
            </a>
          </div>
        )}
      </form>
      {activeModal && (
        <>
          <VerifyPhoneModal
            open={activeModal === ModalTypes.VERIFY_PHONE_MODAL}
            phoneNumber={
              phoneFormData.countryCode + fixedPhoneNumber.current ||
              phoneFormData.phoneNumber
            }
            handleClose={() => dispatch(hideModal())}
          />
        </>
      )}
    </div>
  )
}

export default PhoneLogin
